import React, { Children, useState } from 'react';
import { Drawer, MenuItem, Select, TextField } from '@biq/apex-ui';
import { MultilineTextField } from '../../../../components';
import { EditDrawerCancelModal } from '../drawers/EditDrawerCancelModal';
import { EditDrawerFooter } from '../drawers/EditDrawerFooter';
import {
    attributeErrorMessage,
    indexesStyles,
    indexSchema,
    TypeData,
    typesData,
} from './dictionaryManagerIndexEditDrawer-helper';
import { useIndexList } from 'pages/dictionaries/contexts/IndexListContext';
import { Box, Grid, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DictionaryIndex, getAvailableAttributeList } from 'lib';
import { yupResolver } from '@hookform/resolvers/yup';
import { AttributesComponent } from '../drawers/AttributesComponent';
import { useAttributeList } from 'pages/dictionaries/contexts/AttributeListContext';

interface DictionaryManagerIndexEditDrawerProps {
    setIsChangesUnsaved?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DictionaryManagerIndexEditDrawer = ({
    setIsChangesUnsaved,
}: DictionaryManagerIndexEditDrawerProps): React.JSX.Element => {
    const {
        selectedIndex: index,
        setSelectedIndex,
        setIndex,
        deleteIndex,
    } = useIndexList();
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false);
    const { attributes } = useAttributeList();
    const attributesData = getAvailableAttributeList(attributes);
    const defaultIndexValues = { ...index };
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        getValues,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<DictionaryIndex>({
        mode: 'all',
        defaultValues: defaultIndexValues,
        resolver: yupResolver(indexSchema),
    });

    const handleIndexTypeChanged = (): void => {
        setValue('partialFilterTql', '');
    };

    const onSubmit = (): void => {
        setIsChangesUnsaved?.(true);
        if (!watch('attributes') || watch('attributes')?.length === 0) {
            setError('attributes', {
                message: attributeErrorMessage,
            });
            return;
        }
        clearErrors('attributes');
        const data = getValues();
        if (index.id) {
            setIndex(index.id, data);
        } else {
            const id = crypto.randomUUID();
            const newIndex: DictionaryIndex = {
                ...data,
                id: id,
            };
            setIndex(id, newIndex);
        }

        setSelectedIndex(undefined);
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const handleClose = (): void => {
        handleCancelModal();
        setSelectedIndex();
    };

    const onClose = (): void => {
        if (isValuesChanged) {
            setOpenCancelModal(true);
        } else {
            handleClose();
        }
    };

    const handleDeleteIndex = (): void => {
        setIsChangesUnsaved?.(true);
        deleteIndex(index);
        handleClose();
    };

    const displayPartialTQLFilter = (): boolean => {
        return watch('type') === 'UNIQUE' || watch('type') === 'NORMAL';
    };

    return (
        <Drawer
            closable
            viewMode='edit'
            open
            drawerType={'rounded'}
            anchor={'right'}
            title={'Edit Index'}
            titleStyles={indexesStyles.titleStyles}
            sx={indexesStyles.drawer}
            onClose={onClose}
            footerChildren={
                <EditDrawerFooter
                    mainContainerStyles={indexesStyles.buttonContainerStyles}
                    onDeleteClick={handleDeleteIndex}
                    onSubmitClick={handleSubmit(onSubmit)}
                />
            }
        >
            <Grid sx={indexesStyles.formContainerStyles}>
                <Grid sx={indexesStyles.nameFieldStyles}>
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Name'
                                required
                                onChange={(value): void => {
                                    setIsValuesChanged(true);
                                    field.onChange(value);
                                }}
                                error={!!errors?.name}
                                errorText={
                                    errors.name ? errors?.name?.message : ''
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid sx={indexesStyles.fieldStyles}>
                    <Controller
                        name='type'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <Select
                                {...field}
                                variant='outlined'
                                label={'Type'}
                                required
                                onChange={(value): void => {
                                    setIsValuesChanged(true);
                                    handleIndexTypeChanged();
                                    field.onChange(value);
                                }}
                                error={!!errors.type}
                                errorText={
                                    errors.type ? errors.type.message : ''
                                }
                            >
                                {Children.toArray(
                                    typesData.map((type: TypeData) => (
                                        <MenuItem value={type.type}>
                                            <Box
                                                sx={
                                                    indexesStyles.menuOptionContainerStyles
                                                }
                                            >
                                                <img
                                                    alt='type-icon'
                                                    src={type.typeIcon}
                                                    style={type.typeIconStyles}
                                                />
                                                <Typography
                                                    sx={
                                                        indexesStyles.selectOptionTextStyles
                                                    }
                                                >
                                                    {type.typeLabel}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        )}
                    />
                </Grid>
            </Grid>
            {displayPartialTQLFilter() && (
                <Grid sx={indexesStyles.fieldStyles}>
                    <Controller
                        name='partialFilterTql'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <MultilineTextField
                                {...field}
                                control={control}
                                label='Partial TQL Filter'
                                helperText={`An index entry is created only when certain conditions are met. Write these conditions like a TQL WHERE clause, for example: data.fname == 'Smith'.`}
                                name='partialFilterTql'
                                error={!!errors.partialFilterTql}
                                errorText={
                                    errors.partialFilterTql
                                        ? errors.partialFilterTql.message
                                        : ''
                                }
                                onChange={(): void => {
                                    setIsValuesChanged(true);
                                }}
                            />
                        )}
                    />
                </Grid>
            )}
            <AttributesComponent
                attributesData={attributesData}
                selectedAttributesData={watch('attributes')}
                setValue={setValue}
                setIsValuesChanged={setIsValuesChanged}
                clearErrors={clearErrors}
                errors={errors}
                errorText={errors?.attributes?.message ?? ''}
                required
            />
            {openCancelModal && (
                <EditDrawerCancelModal
                    cancelModal={openCancelModal}
                    handleCancelModal={handleCancelModal}
                    handleCancel={handleClose}
                />
            )}
        </Drawer>
    );
};
